import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, map, takeUntil } from 'rxjs';
import { NetworkHealthService } from '../../../services/network-health.service';
import { NetworkState } from '../../shared/models/network-health.model';

interface NetworkIndicatorVM {
  visible: boolean;       // false when NORMAL → component renders nothing
  state: NetworkState;
  label: string;
  icon: string;           // PrimeNG pi icon class
  cssClass: string;       // drives colour via CSS
  pulsing: boolean;       // true for OFFLINE / CRITICAL
}

/**
 * NetworkStatusIndicatorComponent
 *
 * Displays a small pill in the POS header showing network health status.
 *
 * VISIBILITY RULE (from user requirement):
 *   NORMAL state → completely invisible. Nothing is rendered.
 *   All other states → pill appears with appropriate label and colour.
 *
 * This component is purely presentational — it subscribes to
 * NetworkHealthService and renders the derived view model.
 * It has NO side effects on any business logic.
 *
 * Uses OnPush change detection for performance.
 */
@Component({
  selector: 'app-network-status-indicator',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './network-status-indicator.component.html',
  styleUrl: './network-status-indicator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NetworkStatusIndicatorComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  vm: NetworkIndicatorVM | null = null;

  constructor(
    private readonly networkHealthService: NetworkHealthService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.networkHealthService.networkState$
      .pipe(
        map((state) => this.buildVM(state)),
        takeUntil(this.destroy$)
      )
      .subscribe((vm) => {
        this.vm = vm;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildVM(state: NetworkState): NetworkIndicatorVM {
    switch (state) {
      // Slow-network pill disabled — DEGRADED / SLOW / CRITICAL now fall through to NORMAL (hidden).
      // case NetworkState.DEGRADED:
      //   return {
      //     visible: true,
      //     state,
      //     label: 'Slow',
      //     icon: 'pi pi-wifi',
      //     cssClass: 'state-degraded',
      //     pulsing: false,
      //   };
      // case NetworkState.SLOW:
      //   return {
      //     visible: true,
      //     state,
      //     label: 'Slow',
      //     icon: 'pi pi-wifi',
      //     cssClass: 'state-slow',
      //     pulsing: false,
      //   };
      // case NetworkState.CRITICAL:
      //   return {
      //     visible: true,
      //     state,
      //     label: 'Unstable',
      //     icon: 'pi pi-exclamation-triangle',
      //     cssClass: 'state-critical',
      //     pulsing: true,
      //   };
      case NetworkState.OFFLINE:
        return {
          visible: true,
          state,
          label: 'Offline',
          icon: 'pi pi-times-circle',
          cssClass: 'state-offline',
          pulsing: true,
        };
      // case NetworkState.RECOVERING:
      //   return {
      //     visible: true,
      //     state,
      //     label: 'Reconnecting…',
      //     icon: 'pi pi-refresh',
      //     cssClass: 'state-recovering',
      //     pulsing: false,
      //   };
      case NetworkState.NORMAL:
      default:
        // NORMAL → render nothing
        return {
          visible: false,
          state,
          label: '',
          icon: '',
          cssClass: '',
          pulsing: false,
        };
    }
  }
}
